import { Component } from '@angular/core';
import { PaymentTypeFormModel } from '../../../core/shared/models/paymenttypemodel';
import { TableAction, TableColumn } from '../../shared/models/table-models';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { Setup } from '../../setup/shared/service/setup';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { ToastService } from '../../../core/shared/services/toast.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Popup } from '../../../core/shared/components/popup/popup';
import { Table } from '../../shared/table/table';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { faPencil, faTrash, faSync } from '@fortawesome/free-solid-svg-icons';

export interface PaymentTypeUI {
  paymentTypeId: number;
  status: string;
  clientName: string;
  glPrincipal: string;
  glAccruedMarkup: string;
  glProfitLoss: string;
  facilityType: string | number;
  facilityName?: string;
  clientSeq: number;
}

@Component({
  selector: 'app-client-gl-code',
  imports: [CommonModule, FormsModule, Table, Popup, TranslatePipe],
  templateUrl: './client-gl-code.html',
  styleUrl: './client-gl-code.css',
})
export class ClientGlCode {
  private clientMap: Map<number, string> = new Map();
  private facilityMap: Map<number, string> = new Map();
  clientOptions: { label: string; value: number }[] = [];
  facilityOptions: { label: string; value: number }[] = [];

  faPencil = faPencil;
  faTrash = faTrash;
  faSync = faSync;

  data: PaymentTypeUI[] = [];
  selectedRow: PaymentTypeUI | null = null;

  showPopup = false;
  popupTitle = '';
  popupType: 'form' | 'confirm' = 'form'; // ✅ controls popup mode
  popupMessage = ''; // ✅ delete confirmation message

  typeCategoryKey: number = 0;
  productNumKey: number = 13;
  popupFields: DynamicFieldConfig[] = [];

  constructor(
    private setupService: Setup,
    private pickListService: PickListService,
    private toast: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.setupService.getclientList().subscribe((clients: any[]) => {
      clients.forEach((c) => {
        this.clientMap.set(c.clientSeq, c.clntNm);
      });

      this.clientOptions = clients
        .map((c) => ({
          label: (c.clntNm || '').trim(),
          value: c.clientSeq,
        }))
        .sort((a, b) => {
          const aStartsWithNumber = /^\d/.test(a.label);
          const bStartsWithNumber = /^\d/.test(b.label);

          if (aStartsWithNumber && !bStartsWithNumber) return 1;
          if (!aStartsWithNumber && bStartsWithNumber) return -1;

          return a.label.localeCompare(b.label, undefined, {
            sensitivity: 'base',
            numeric: true,
          });
        });

      this.loadPaymentTypes();
    });
    this.pickListService.getOptions(this.productNumKey).subscribe((options: any[]) => {
      options.forEach((opt) => this.facilityMap.set(opt.value, opt.label));
      this.facilityOptions = options;
    });
  }

  /* ================= LOAD ================= */
  loadPaymentTypes(): void {
    this.setupService.getTypes(this.typeCategoryKey).subscribe({
      next: (res: any[]) => {
        this.data = res.map((item) => ({
          paymentTypeId: item.typSeq,
          clientSeq: item.clntSeq,
          clientName: this.clientMap.get(item.clntSeq) || '',
          facilityType: item.prdTypKey,
          facilityName: this.facilityMap.get(item.prdTypKey) || '',
          glPrincipal: item.glPrnNum || '',
          glAccruedMarkup: item.glMrkupNum || '',
          glProfitLoss: item.glPlNum || '',
          status: item.typStsKey === 1 ? 'ACTIVE' : 'INACTIVE',
        }));

        this.data.sort((a, b) => {
          const nameA = (a.clientName || '').trim();
          const nameB = (b.clientName || '').trim();

          const aStartsWithNumber = /^\d/.test(nameA);
          const bStartsWithNumber = /^\d/.test(nameB);

          if (aStartsWithNumber && !bStartsWithNumber) return 1;
          if (!aStartsWithNumber && bStartsWithNumber) return -1;

          // Dono same type → normal sort
          return nameA.localeCompare(nameB, undefined, {
            sensitivity: 'base',
            numeric: true,
          });
        });
      },
      error: (err) => console.error(err),
    });
  }

  /* ================= TABLE ================= */
  columns: TableColumn[] = [
    { key: 'clientName', label: 'GL.CLIENT_GL.CLIENT_NAME' },
    { key: 'facilityName', label: 'GL.FACILITY_TYPE' },
    { key: 'glPrincipal', label: 'GL.GL_PRINCIPAL' },
    { key: 'glAccruedMarkup', label: 'GL.GL_ACCRUED_MARKUP' },
    { key: 'glProfitLoss', label: 'GL.GL_PROFIT_LOSS' },
    {
      key: 'status',
      label: 'COMMON.STATUS',
      type: 'map',
      map: { ACTIVE: 'GL.STATUS_ACTIVE', INACTIVE: 'GL.STATUS_INACTIVE' },
    },
  ];

  actions: TableAction[] = [
    {
      icon: this.faPencil,
      class: 'text-green-600',
      tooltip: 'COMMON.EDIT',
      onClick: (row: PaymentTypeUI) => this.openEditPopup(row),
    },
    {
      icon: this.faTrash,
      class: 'text-red-600',
      tooltip: 'COMMON.DELETE',
      onClick: (row: PaymentTypeUI) => this.openDeletePopup(row),
    },
    {
      icon: this.faSync,
      class: 'text-black',
      tooltip: 'COMMON.REFRESH',
      onClick: () => this.loadPaymentTypes(),
    },
  ];

  /* ================= POPUP OPENERS ================= */
  openAddPopup(): void {
    this.popupTitle = 'GL.CLIENT_GL.ADD';
    this.popupType = 'form';
    this.selectedRow = null;
    this.popupFields = [
      {
        key: 'clientSeq',
        label: 'GL.CLIENT_GL.CLIENT',
        type: 'select',
        options$: this.clientOptions,
        validations: { required: true },
      },
      {
        key: 'facility',
        label: 'GL.FACILITY_TYPE',
        type: 'select',
        value: null,
        options$: this.pickListService.getOptions(13),
        validations: { required: true },
      },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'select',
        value: 'ACTIVE',
        options$: [
          { label: 'COMMON.ACTIVE', value: 'ACTIVE' },
          { label: 'COMMON.INACTIVE', value: 'INACTIVE' },
        ],
      },
      { key: 'glPrincipal', label: 'GL.GL_PRINCIPAL', type: 'text' },
      { key: 'glAccruedMarkup', label: 'GL.GL_ACCRUED_MARKUP', type: 'text' },
      { key: 'glProfitLoss', label: 'GL.GL_PROFIT_LOSS', type: 'text' },
    ];
    this.showPopup = true;
  }

  openEditPopup(row: PaymentTypeUI): void {
    this.popupTitle = 'GL.CLIENT_GL.EDIT';
    this.popupType = 'form';
    this.selectedRow = row;
    this.popupFields = [
      {
        key: 'clientSeq',
        label: 'GL.CLIENT_GL.CLIENT',
        type: 'select',
        options$: this.clientOptions,
        value: row.clientSeq,
        validations: { required: true },
      },
      {
        key: 'facility',
        label: 'GL.FACILITY_TYPE',
        type: 'select',
        options$: this.pickListService.getOptions(13),
        value: Number(row.facilityType),
        validations: { required: true },
      },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'select',
        value: row.status,
        options$: [
          { label: 'COMMON.ACTIVE', value: 'ACTIVE' },
          { label: 'COMMON.INACTIVE', value: 'INACTIVE' },
        ],
      },
      { key: 'glPrincipal', label: 'GL.GL_PRINCIPAL', type: 'text', value: row.glPrincipal },
      {
        key: 'glAccruedMarkup',
        label: 'GL.GL_ACCRUED_MARKUP',
        type: 'text',
        value: row.glAccruedMarkup,
      },
      { key: 'glProfitLoss', label: 'GL.GL_PROFIT_LOSS', type: 'text', value: row.glProfitLoss },
    ];
    this.showPopup = true;
  }

  openDeletePopup(row: PaymentTypeUI): void {
    this.popupTitle = 'GL.CLIENT_GL.DELETE';
    this.popupType = 'confirm'; // ✅ confirm mode
    this.selectedRow = row;
    this.popupMessage = this.translate.instant('GL.CLIENT_GL.DELETE_CONFIRM', { name: row.clientName });
    this.showPopup = true;
  }

  /* ================= SAVE / UPDATE ================= */
  handleSave(formData: PaymentTypeFormModel): void {
    // Duplicate check (only for add/edit)
    const formClient = Number(formData.clientSeq);
    const formFacility = Number(formData.facility);
    const isDuplicate = this.data.some((row) => {
      if (this.selectedRow && row.paymentTypeId === this.selectedRow.paymentTypeId) return false;
      return Number(row.clientSeq) === formClient && Number(row.facilityType) === formFacility;
    });

    if (isDuplicate) {
      this.toast.error('GL.CLIENT_GL.DUPLICATE');
      return;
    }

    const payload = {
      typSeq: this.selectedRow ? this.selectedRow.paymentTypeId : null,
      typStr: '',
      typId: '',
      typCtgryKey: 0,
      clntSeq: formData.clientSeq,
      prdTypKey: Number(formData.facility),
      typStsKey: formData.status === 'ACTIVE' ? 1 : 0,
      glPrnNum: formData.glPrincipal || '',
      glMrkupNum: formData.glAccruedMarkup || '',
      glPlNum: formData.glProfitLoss || '',
      perdFlg: '',
      dfrdAcctNum: '',
      bddtAcctNum: '',
      brnchSeq: '',
      delFlg: 0,
    };

    this.setupService.savetype(payload).subscribe({
      next: () => {
        this.toast.success(this.selectedRow ? 'GL.UPDATED_SUCCESSFULLY' : 'GL.SAVED_SUCCESSFULLY');
        this.closePopup();
        this.loadPaymentTypes();
      },
      error: (err) => {
        console.error('Save failed', err);
        this.toast.error('GL.OPERATION_FAILED');
      },
    });
  }

  /* ================= DELETE ================= */
  handleDelete(): void {
    if (!this.selectedRow) return;

    const payload = {
      typSeq: this.selectedRow.paymentTypeId,
      typStr: '',
      typId: '',
      typCtgryKey: 0,
      clntSeq: this.selectedRow.clientSeq,
      prdTypKey: Number(this.selectedRow.facilityType),
      typStsKey: this.selectedRow.status === 'ACTIVE' ? 1 : 0,
      glPrnNum: this.selectedRow.glPrincipal,
      glMrkupNum: this.selectedRow.glAccruedMarkup,
      glPlNum: this.selectedRow.glProfitLoss,
      perdFlg: '',
      dfrdAcctNum: '',
      bddtAcctNum: '',
      brnchSeq: '',
      delFlg: 1, // ✅ delete flag
    };

    this.setupService.savetype(payload).subscribe({
      next: () => {
        this.toast.success('GL.DELETED_SUCCESSFULLY');
        this.closePopup();
        this.loadPaymentTypes();
      },
      error: (err) => {
        console.error('Delete failed', err);
        this.toast.error('GL.DELETE_FAILED');
      },
    });
  }

  private closePopup(): void {
    this.showPopup = false;
    this.selectedRow = null;
  }
}
